import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import moment from 'moment';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AddUserDialogComponent, UserAction } from '../../../accounts/add-user-dialog.component';
import { User } from '../../../core/user';

export function setDateOnDateTime(current: Date, newDate: Date): Date {
  if (!current || !newDate) {
    return current || newDate || null;
  }
  newDate.setHours(current.getHours(), current.getMinutes(), current.getSeconds(), current.getMilliseconds());
  return newDate;
}

export function setTimeOnDateTime(current: Date, newTime: string): Date {
  if (!current || !newTime) {
    return current || null;
  }
  const newDate = moment(
    `${current.getFullYear()}-${current.getMonth() + 1}-${current.getDate()}T${newTime}`,
    'YYYY-MM-DDTHH:mm A',
  ).toDate();
  if (isNaN(newDate.getTime())) {
    return current;
  }
  return newDate;
}

@Component({
  selector: 'app-collect-payment',
  templateUrl: './collect-payment.component.html',
  styleUrls: ['./collect-payment.component.scss', '../order-summary/order-summary.component.scss'],
})
export class CollectPaymentComponent implements OnInit, OnDestroy {
  @Input() requireApproval = false;
  @Output() requireApprovalChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input() users: User[];

  @Input() subjectId!: string;
  @Output() subjectIdChange: EventEmitter<string> = new EventEmitter<string>();

  @Input() hasExpiryDate!: boolean;
  @Output() hasExpiryDateChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input() expiryDate: Date;
  @Output() expiryDateChange: EventEmitter<Date> = new EventEmitter<Date>();

  @Input() accountGroupId: string;
  @Input() partnerId: string;

  private selectedUserId$$ = new BehaviorSubject<string>('');
  selectedUserId$ = this.selectedUserId$$.asObservable();

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  minDate = new Date();
  expiryTimeControl = new UntypedFormControl('', [Validators.required]);

  constructor(
    private readonly analyticsService: ProductAnalyticsService,
    public dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.expiryTimeControl.setValue(this.expiryDate ? moment(this.expiryDate).format('HH:mm') : '17:00');
    this.expiryTimeControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => this.handleExpiryTimeChange(value));

    this.selectedUserId$$.next(this.subjectId);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  handleExpiryDateChange(date: Date): void {
    this.expiryDateChange.emit(setDateOnDateTime(this.expiryDate, new Date(date)));
  }

  handleExpiryTimeChange(time: string): void {
    this.expiryDateChange.emit(setTimeOnDateTime(this.expiryDate, time));
  }

  trackToggle(e): void {
    this.analyticsService.trackToggle('clicked-collect-order-payment', e.value);
  }

  preventBlur(e: Event): void {
    e.stopPropagation();
    e.preventDefault();
  }

  presentAddUsersDialog(): void {
    const dialogRef = this.dialog.open(AddUserDialogComponent, {
      width: '620px',
    });
    dialogRef.componentInstance.accountGroupId = this.accountGroupId;
    dialogRef.componentInstance.partnerId = this.partnerId;
    dialogRef.componentInstance.existingUsers = this.users;
    dialogRef.componentInstance.showCreateTab = true;
    dialogRef.afterClosed().subscribe((result) => {
      if (result?.action === UserAction.CREATE || result?.action === UserAction.ADD) {
        if (result?.users?.length === 0) {
          return;
        }
        const userId = result.users[0].userId;
        this.selectedUserId$$.next(userId);
        this.subjectIdChange.emit(userId);
      }
    });
  }
}
